import React from "react"
import styles from "./whatsNewModal.module.css"
import Button from "../Form/Button"
import { MdAutoAwesome, MdChevronLeft, MdChevronRight, MdClose, MdOpenInNew, MdOutlineBuild, MdOutlineCheckCircle, MdOutlineRocketLaunch, MdOutlineSettingsSuggest, MdOutlineSyncAlt } from "react-icons/md"

const categoryLabels = {
    new: "Novo recurso",
    improvement: "Melhoria",
    fix: "Correção",
    automation: "Automação",
    integration: "Integração",
}

const iconMap = {
    new: <MdOutlineRocketLaunch />,
    improvement: <MdAutoAwesome />,
    fix: <MdOutlineBuild />,
    automation: <MdOutlineSettingsSuggest />,
    integration: <MdOutlineSyncAlt />,
}

const impactLabels = {
    informative: "Informativo",
    requires_configuration: "Requer configuração",
    automatic: "Disponível automaticamente",
    new_feature: "Nova funcionalidade",
    fix: "Correção",
}

const formatUpdateDate = (date) => {
    if (!date) return { dayMonth: "", year: "" }

    const parsedDate = new Date(date)
    return {
        dayMonth: parsedDate.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" }).replace(".", ""),
        year: parsedDate.toLocaleDateString("pt-BR", { year: "numeric" }),
    }
}

const WhatsNewModal = ({ update, onClose, onViewAll, showViewAll = true, onPrevious, onNext, hasPrevious = false, hasNext = false }) => {
    if (!update) return null

    const highlights = update?.highlights?.length > 0 ? update.highlights : [
        { title: update.title, description: update.summary, icon: update.category },
    ]
    const accentColor = update?.accent_color || "#6245ae"
    const updateDate = formatUpdateDate(update.published_at)
    const hasPrimaryAction = update?.primary_action?.label && update?.primary_action?.url
    const links = Array.isArray(update?.links) ? update.links.filter(link => link?.label && link?.url) : []
    const sideLinks = links.slice(0, 2)
    const contentLinks = links.slice(2)

    const openActionLink = (action) => {
        if (!action?.url) return

        if (action.type === "external" || /^https?:\/\//i.test(action.url)) {
            window.open(action.url, "_blank", "noopener,noreferrer")
            return
        }

        const accountId = window.location.pathname.split("/").filter(Boolean)?.[0]
        const shouldPrefixAccount = accountId && /^[0-9a-fA-F]{24}$/.test(accountId) && action.url.startsWith("/") && !action.url.startsWith(`/${accountId}/`)
        window.location.href = shouldPrefixAccount ? `/${accountId}${action.url}` : action.url
    }

    return (
        <div className={styles.backdrop} style={{ "--update-accent": accentColor }}>
            <div className={styles.modal}>
                <div className={styles.visual}>
                    {update?.image_url && <img className={styles.image} src={update.image_url} alt="" />}
                    <div className={styles.visualContent}>
                        <span className={styles.badge}>
                            {iconMap[update.category] || iconMap.improvement}
                            {categoryLabels[update.category] || "Novidade"}
                        </span>
                        <div className={styles.version}>
                            <strong>{updateDate.dayMonth}</strong>
                            <span>{updateDate.year} · Versão {update.version}</span>
                        </div>
                    </div>
                    {(hasPrimaryAction || sideLinks.length > 0) &&
                        <div className={styles.sideActions}>
                            {hasPrimaryAction &&
                                <Button className={styles.sidePrimaryAction} onClick={() => openActionLink(update.primary_action)}>
                                    {update.primary_action.label}
                                    <MdOpenInNew />
                                </Button>
                            }
                            {sideLinks.length > 0 &&
                                <div className={styles.sideLinks}>
                                    {sideLinks.map((link, index) => (
                                        <button type="button" key={`${link.label}-${index}`} onClick={() => openActionLink(link)}>
                                            <span>{link.label}</span>
                                            <MdOpenInNew />
                                        </button>
                                    ))}
                                </div>
                            }
                        </div>
                    }
                    {(hasPrevious || hasNext) &&
                        <div className={styles.navigator}>
                            <span>Navegar</span>
                            <div className={styles.navigatorButtons}>
                                <button type="button" onClick={onPrevious} disabled={!hasPrevious} aria-label="Atualização anterior" title="Atualização anterior">
                                    <MdChevronLeft />
                                </button>
                                <button type="button" onClick={onNext} disabled={!hasNext} aria-label="Próxima atualização" title="Próxima atualização">
                                    <MdChevronRight />
                                </button>
                            </div>
                        </div>
                    }
                </div>
                <div className={styles.content}>
                    <div className={styles.header}>
                        <div className={styles.titleGroup}>
                            <span className={styles.eyebrow}>O que mudou</span>
                            <h2 className={styles.title}>{update.title}</h2>
                            {update?.impact &&
                                <span className={styles.impact}>{impactLabels[update.impact] || impactLabels.informative}</span>
                            }
                        </div>
                        <button type="button" className={styles.close} onClick={onClose} aria-label="Fechar" title="Fechar">
                            <MdClose />
                        </button>
                    </div>
                    <p className={styles.summary}>{update.summary}</p>
                    {update?.details &&
                        <div className={styles.details}>
                            <strong>Detalhes</strong>
                            <p>{update.details}</p>
                        </div>
                    }
                    <div className={styles.highlights}>
                        {highlights.slice(0, 4).map((highlight, index) => (
                            <div className={styles.highlight} key={`${highlight.title}-${index}`}>
                                <div className={styles.highlightIcon}>
                                    {iconMap[highlight.icon] || iconMap[update.category] || <MdOutlineCheckCircle />}
                                </div>
                                <div className={styles.highlightText}>
                                    <strong>{highlight.title}</strong>
                                    {highlight.description && <span>{highlight.description}</span>}
                                </div>
                            </div>
                        ))}
                    </div>
                    {contentLinks.length > 0 &&
                        <div className={styles.relatedActions}>
                            <strong>Links relacionados</strong>
                            <div className={styles.links}>
                                {contentLinks.map((link, index) => (
                                    <button type="button" key={`${link.label}-${index}`} onClick={() => openActionLink(link)}>
                                        <span>{link.label}</span>
                                        <MdOpenInNew />
                                    </button>
                                ))}
                            </div>
                        </div>
                    }
                    <div className={styles.actions}>
                        {showViewAll && <Button variant="neutral" onClick={onViewAll}>Ver todas</Button>}
                        <Button variant="secondary" onClick={onClose}>Entendi</Button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default WhatsNewModal
